import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { useSelectedOrganization } from "@/hooks/useCurrentUser";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import type { Brand } from "@/hooks/useGetBrands";

/** Optimistic: the brand leaves the list at once and comes back where it was on failure. */
export function useDeleteBrand() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useSelectedOrganization();
  const { selectedBrandId, setSelectedBrandId } = useWorkspaceStore();
  const orgId = selectedOrganization?.organization.id ?? null;
  const key = ["brands", orgId];

  return useMutation({
    mutationFn: async (brandId: string) => {
      await axiosInstance.delete(`/brand/${brandId}`);
    },
    onMutate: async (brandId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const list = queryClient.getQueryData<Brand[]>(key);
      const index = list?.findIndex((b) => b.id === brandId) ?? -1;
      const removed = index >= 0 ? list![index] : null;
      queryClient.setQueryData<Brand[]>(key, (old) => old?.filter((b) => b.id !== brandId));
      const wasSelected = selectedBrandId === brandId;
      if (wasSelected) setSelectedBrandId(null);
      return { removed, index, wasSelected };
    },
    onSuccess: (_data, brandId) => {
      queryClient.removeQueries({ queryKey: ["brandUniverseGraph", brandId] });
    },
    onError: (_err, _brandId, context) => {
      if (!context?.removed) return;
      const { removed, index, wasSelected } = context;
      queryClient.setQueryData<Brand[]>(key, (old) => {
        const next = [...(old ?? [])];
        if (next.some((b) => b.id === removed.id)) return next;
        next.splice(Math.max(0, index), 0, removed);
        return next;
      });
      if (wasSelected) setSelectedBrandId(removed.id);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
